import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Images, Plus, Trash2, Pencil, ArrowUp, ArrowDown, Upload, ExternalLink } from "lucide-react";
import { ConfirmDialog, EmptyState, Modal } from "@/components/panel/ui";
import BannerSlide from "@/components/store/BannerSlide";
import { cn } from "@/lib/utils";
import { Badge, Button, Card, Field, Input, PageHeader } from "@/components/ui-kit";

const VAZIO = {
  image: "", alt: "", title: "", subtitle: "", cta_label: "", link: "",
  starts_at: "", ends_at: "", active: true,
};

const nulo = (v) => (String(v ?? "").trim() === "" ? null : String(v).trim());
const paraData = (iso) => (iso ? String(iso).slice(0, 10) : "");
const br = (d) => (d ? paraData(d).split("-").reverse().join("/") : null);

/**
 * Por que este banner está (ou não está) na loja.
 *
 * A pergunta que traz alguém a esta tela quase nunca é "quais banners existem" —
 * é "por que a campanha não apareceu". Um selo de ativo/inativo não responde
 * isso quando o motivo é a data, então o estado aqui junta as duas coisas.
 */
function situacao(b) {
  if (!b.active) return { tom: "neutral", texto: "Desligado" };
  if (b.no_ar) return { tom: "success", texto: "No ar" };
  const hoje = new Date().toISOString().slice(0, 10);
  if (b.starts_at && paraData(b.starts_at) > hoje) return { tom: "info", texto: `Entra ${br(b.starts_at)}` };
  return { tom: "danger", texto: `Venceu ${br(b.ends_at)}` };
}

export default function Banners() {
  const [banners, setBanners] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VAZIO);
  const [removendo, setRemovendo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const arquivo = useRef(null);

  const carregar = () => {
    setCarregando(true); setErro(false);
    api.get("/admin/banners")
      .then((r) => setBanners(r.data || []))
      .catch(() => setErro(true))
      .finally(() => setCarregando(false));
  };

  useEffect(carregar, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const abrirNovo = () => { setForm(VAZIO); setEditando("novo"); };
  const abrirEdicao = (b) => {
    setForm({
      image: b.image, alt: b.alt || "", title: b.title || "", subtitle: b.subtitle || "",
      cta_label: b.cta_label || "", link: b.link || "",
      starts_at: paraData(b.starts_at), ends_at: paraData(b.ends_at), active: b.active,
    });
    setEditando(b);
  };

  const enviarArte = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
    } catch (err) { toast.error(apiError(err)); }
    finally { setEnviando(false); e.target.value = ""; }
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error("Envie a arte do banner"); return; }
    if (form.starts_at && form.ends_at && form.ends_at < form.starts_at) {
      toast.error("A data final não pode ser anterior à inicial"); return;
    }

    const payload = {
      image: form.image,
      alt: nulo(form.alt), title: nulo(form.title), subtitle: nulo(form.subtitle),
      cta_label: nulo(form.cta_label), link: nulo(form.link),
      starts_at: nulo(form.starts_at), ends_at: nulo(form.ends_at),
      active: !!form.active,
    };

    setSalvando(true);
    try {
      if (editando === "novo") { await api.post("/admin/banners", payload); toast.success("Banner criado"); }
      else { await api.patch(`/admin/banners/${editando.id}`, payload); toast.success("Banner atualizado"); }
      setEditando(null); carregar();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSalvando(false); }
  };

  const alternar = async (b) => {
    try {
      await api.patch(`/admin/banners/${b.id}`, { active: !b.active });
      carregar();
    } catch (e) { toast.error(apiError(e)); }
  };

  /**
   * Sobe e desce, em vez de arrastar.
   *
   * Arrastar é mais bonito e é inalcançável por teclado sem uma implementação
   * bem maior. Com três ou quatro banners, dois botões resolvem a mesma coisa e
   * funcionam para todo mundo.
   */
  const mover = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= banners.length) return;
    const nova = [...banners];
    [nova[i], nova[j]] = [nova[j], nova[i]];
    setBanners(nova);   // otimista: a fila reordena na hora, sem piscar
    try {
      await api.post("/admin/banners/reorder", { ids: nova.map((b) => b.id) });
    } catch (e) { toast.error(apiError(e)); carregar(); }
  };

  const remover = async (b) => {
    try { await api.delete(`/admin/banners/${b.id}`); toast.success("Banner removido"); carregar(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-banners-page">
      <PageHeader
        eyebrow="Loja"
        title="Banners da Home"
        icon={Images}
        description="As artes que passam no topo da loja. A ordem aqui é a ordem do slider, e o período faz a campanha entrar e sair sozinha."
        actions={<Button icon={Plus} onClick={abrirNovo} data-testid="banner-new-btn">Novo banner</Button>}
      />

      {/* LISTA VISUAL, e não tabela.
      
          Banner é conteúdo 100% visual e ordenado — uma miniatura de 96px numa
          linha de tabela não responde a pergunta que traz alguém aqui: "como
          isso vai ficar?". Cada cartão desenha o slide DE VERDADE (mesmo
          componente da home), na ordem em que o cliente vai ver, com o número
          da posição na frente. */}
      {carregando && <Card className="p-8 text-center text-sm text-slate-400">Carregando banners…</Card>}

      {erro && (
        <Card className="p-8 text-center">
          <p className="text-sm text-slate-600">Não foi possível carregar os banners.</p>
          <Button variant="secondary" onClick={carregar} className="mt-3">Tentar de novo</Button>
        </Card>
      )}

      {!carregando && !erro && banners.length === 0 && (
        <Card>
          <EmptyState
            icon={Images}
            title="Nenhum banner cadastrado"
            desc="Sem banner, a home abre direto pela seção informativa — nada quebra. Crie o primeiro para a loja ganhar a vitrine do topo."
            action={<Button icon={Plus} onClick={abrirNovo}>Criar banner</Button>}
            testId="banners-empty"
          />
        </Card>
      )}

      {!carregando && !erro && banners.length > 0 && (
        <ol className="space-y-3" data-testid="banners-list">
          {banners.map((b, i) => {
            const s = situacao(b);
            const foraDoAr = !b.no_ar;
            return (
              <li key={b.id}>
                <Card className={cn("overflow-hidden", foraDoAr && "opacity-70")} data-testid="banner-card">
                  <div className="flex flex-col sm:flex-row">
                    {/* Ordem: número grande + setas. Num grupo próprio, à
                        esquerda, longe das ações destrutivas — antes as cinco
                        coisas dividiam a mesma célula apertada. */}
                    <div className="flex sm:flex-col items-center justify-center gap-1 px-3 py-2 sm:py-4 bg-slate-50 border-b sm:border-b-0 sm:border-r border-slate-100 shrink-0">
                      <span className="font-mono text-lg font-semibold text-slate-400 tabular-nums w-6 text-center" aria-hidden>{i + 1}</span>
                      <div className="flex sm:flex-col gap-0.5">
                        <Button variant="ghost" size="iconSm" onClick={() => mover(i, -1)} disabled={i === 0}
                          aria-label={`Mover ${b.title || "banner"} para a posição ${i}`} data-testid="banner-up"><ArrowUp size={15} /></Button>
                        <Button variant="ghost" size="iconSm" onClick={() => mover(i, 1)} disabled={i === banners.length - 1}
                          aria-label={`Mover ${b.title || "banner"} para a posição ${i + 2}`} data-testid="banner-down"><ArrowDown size={15} /></Button>
                      </div>
                    </div>

                    {/* A prévia real, no formato do slide da home. */}
                    <div className="relative w-full sm:w-[19rem] shrink-0 aspect-[21/9] bg-slate-900 overflow-hidden">
                      <BannerSlide banner={b} size="preview" />
                    </div>

                    <div className="flex-1 min-w-0 p-4 flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-medium text-slate-900 leading-snug min-w-0">
                          {b.title || <span className="text-slate-400 font-normal">Arte sem texto sobreposto</span>}
                        </p>
                        <Badge tone={s.tom}>{s.texto}</Badge>
                      </div>

                      <dl className="text-xs text-slate-500 space-y-1">
                        <div className="flex gap-1.5">
                          <dt className="text-slate-400">Leva para:</dt>
                          <dd className="min-w-0 truncate">
                            {b.link
                              ? <span className="text-brand-600 inline-flex items-center gap-1"><ExternalLink size={11} aria-hidden />{b.link}</span>
                              : <span className="text-slate-400">nenhum lugar — só avisa</span>}
                          </dd>
                        </div>
                        <div className="flex gap-1.5">
                          <dt className="text-slate-400">Período:</dt>
                          <dd>
                            {b.starts_at || b.ends_at
                              ? [b.starts_at && `de ${br(b.starts_at)}`, b.ends_at && `até ${br(b.ends_at)}`].filter(Boolean).join(" ")
                              : "sem prazo definido"}
                          </dd>
                        </div>
                      </dl>

                      <div className="mt-auto pt-2 flex flex-wrap items-center gap-2">
                        <button type="button" onClick={() => alternar(b)} aria-pressed={!!b.active}
                          aria-label={b.active ? `Desligar ${b.title || "banner"}` : `Ligar ${b.title || "banner"}`}
                          data-testid="banner-toggle"
                          className={cn("text-xs font-semibold px-3 h-9 md:h-7 rounded-full transition-colors",
                            b.active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-200")}>
                          {b.active ? "Ativo" : "Inativo"}
                        </button>
                        <div className="grow" />
                        {/* Editar com RÓTULO: é a ação que se usa aqui o tempo
                            todo, e um ícone solto ao lado da lixeira convida ao
                            clique errado. */}
                        <Button variant="secondary" size="sm" icon={Pencil} onClick={() => abrirEdicao(b)}
                          data-testid="banner-edit">Editar</Button>
                        <Button variant="ghost" size="iconSm" onClick={() => setRemovendo(b)} className="hover:text-red-500"
                          aria-label={`Excluir ${b.title || "banner"}`} data-testid="banner-delete"><Trash2 size={15} /></Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ol>
      )}

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title={editando === "novo" ? "Novo banner" : "Editar banner"}
        maxWidth="max-w-2xl"
        testId="banner-form-modal"
      >
        <form onSubmit={salvar} className="space-y-5">
          {/* PRÉVIA AO VIVO, no topo do formulário.
          
              É o mesmo componente que a home usa, então o que aparece aqui é o
              que o cliente vai ver. Existe por um motivo específico: o texto
              sobreposto é branco, e o erro mais comum é escolher uma arte clara
              e só descobrir que o título sumiu depois de publicar. Aqui a
              pessoa vê enquanto digita. */}
          {form.image && (
            <div>
              <p className="text-eyebrow uppercase text-slate-500 mb-1.5">Como vai aparecer na loja</p>
              <div className="relative w-full aspect-[21/9] rounded-lg overflow-hidden bg-slate-900 border border-slate-200"
                data-testid="banner-live-preview">
                <BannerSlide banner={form} size="preview" />
              </div>
            </div>
          )}

          <Field label="Arte" hint="Formato deitado, 1920×820 ou proporção parecida. No celular a arte é cortada nas laterais — deixe o essencial no centro.">
            <div className="flex items-start gap-4">
              {/* Miniatura só ENQUANTO não há arte: com arte, a prévia ao vivo
                  logo acima já mostra a imagem inteira, e repetir num
                  quadradinho de 176px era a mesma informação duas vezes. */}
              {!form.image && (
                <div className="h-24 w-44 rounded-lg border-2 border-dashed border-slate-200 grid place-items-center text-slate-300 shrink-0">
                  <Images size={22} aria-hidden />
                </div>
              )}
              <div>
                {/* Fora do Tab de propósito: quem opera é o botão visível ao
                    lado. Um campo escondido e focável faria o teclado parar num
                    controle que ninguém vê. O rótulo existe para o leitor de
                    tela que chega nele por outro caminho. */}
                <input ref={arquivo} type="file" accept="image/*" onChange={enviarArte}
                  aria-label="Escolher a arte do banner" tabIndex={-1}
                  className="hidden" data-testid="banner-file-input" />
                <Button type="button" variant="secondary" icon={Upload} loading={enviando}
                  onClick={() => arquivo.current?.click()} data-testid="banner-upload-btn">
                  {form.image ? "Trocar arte" : "Enviar arte"}
                </Button>
              </div>
            </div>
          </Field>

          <Field label="Descrição da imagem" hint="Lida por quem usa leitor de tela. Descreva a promoção, não o arquivo.">
            <Input value={form.alt} onChange={set("alt")} maxLength={200}
              placeholder="Ex.: Cadeira de rodas com 20% de desconto até sexta" data-testid="banner-alt-input" />
          </Field>

          <fieldset className="border-t border-slate-200 pt-4">
            <legend className="sr-only">Texto sobreposto</legend>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-1">Texto sobre a arte</p>
            <p className="text-xs text-slate-500 mb-3">
              Deixe tudo em branco se a arte já traz o texto embutido — o slide entra como imagem pura.
            </p>
            <div className="space-y-3">
              <Field label="Título">
                <Input value={form.title} onChange={set("title")} maxLength={120} data-testid="banner-title-input" />
              </Field>
              <Field label="Subtítulo">
                <Input value={form.subtitle} onChange={set("subtitle")} maxLength={200} data-testid="banner-subtitle-input" />
              </Field>
              <Field label="Texto do botão" hint="Ex.: Ver ofertas">
                <Input value={form.cta_label} onChange={set("cta_label")} maxLength={40} data-testid="banner-cta-input" />
              </Field>

              {/* LARGURA INTEIRA: em meia coluna o campo mostrava uns 40
                  caracteres, e conferir uma URL de campanha com UTM colada ali
                  era impossível. Link é o campo mais longo do formulário. */}
              <Field
                label="Link de destino"
                hint="Endereço completo (https://instagram.com/sualoja) ou uma página da própria loja começando com barra (/produtos?promo=1). Em branco, o banner só avisa e não vira link."
              >
                <Input value={form.link} onChange={set("link")} maxLength={600}
                  placeholder="https://…  ou  /produtos" data-testid="banner-link-input" />
              </Field>

              {/* Diz PARA ONDE vai, enquanto se digita. Externo abre em nova
                  aba e interno não — é a diferença que a pessoa não tem como
                  deduzir do texto que ela mesma colou. */}
              {form.link.trim() && (
                <p className="text-xs -mt-2 flex items-center gap-1.5">
                  {/^https?:\/\//i.test(form.link.trim()) ? (
                    <><ExternalLink size={12} className="text-brand-600" aria-hidden />
                      <span className="text-slate-500">Site externo — abre em uma nova aba.</span></>
                  ) : form.link.trim().startsWith("/") ? (
                    <span className="text-slate-500">Página da própria loja — abre na mesma aba.</span>
                  ) : (
                    <span className="text-amber-600">
                      Sem <b>https://</b> no começo. Ao salvar vira <b>https://{form.link.trim()}</b> — se
                      era para ser uma página da loja, comece com barra.
                    </span>
                  )}
                </p>
              )}
            </div>
          </fieldset>

          <fieldset className="border-t border-slate-200 pt-4">
            <legend className="sr-only">Período de exibição</legend>
            <p className="text-[11px] font-semibold uppercase text-slate-500 mb-2">Período</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Entra em" hint="Em branco: já entra.">
                <Input type="date" value={form.starts_at} onChange={set("starts_at")} data-testid="banner-from-input" />
              </Field>
              <Field label="Sai em" hint="Em branco: fica até desligar.">
                <Input type="date" value={form.ends_at} onChange={set("ends_at")} data-testid="banner-until-input" />
              </Field>
            </div>
          </fieldset>

          <div className="flex gap-3 justify-end border-t border-slate-200 pt-4">
            <Button type="button" variant="ghost" onClick={() => setEditando(null)}>Cancelar</Button>
            <Button type="submit" loading={salvando} data-testid="banner-save-btn">
              {editando === "novo" ? "Criar banner" : "Salvar"}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!removendo}
        onClose={() => setRemovendo(null)}
        onConfirm={() => remover(removendo)}
        title="Excluir banner"
        message={removendo ? `Excluir "${removendo.title || "este banner"}"? Ele sai da home imediatamente. A imagem enviada continua no servidor.` : ""}
        confirmLabel="Excluir"
      />

      {/* O que a lista NÃO consegue mostrar: a ordem já está nos números, mas o
          comportamento do carrossel na loja não aparece numa imagem parada. */}
      {banners.length > 1 && (
        <Card className="p-4 text-xs text-slate-500 leading-relaxed">
          Na loja o slider troca sozinho a cada 6 segundos. Ele <b>para</b> quando o visitante passa o
          mouse, navega pelo teclado ou aperta o botão de pausa — e nem começa para quem pediu menos
          animação no sistema.
        </Card>
      )}
    </div>
  );
}
